import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin'
import { MarkdownShortcutPlugin } from '@lexical/react/LexicalMarkdownShortcutPlugin'
import { EditorRefPlugin } from '@lexical/react/LexicalEditorRefPlugin'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { $isListNode, ListNode } from '@lexical/list'
import { $isHeadingNode, $createHeadingNode, $createQuoteNode } from '@lexical/rich-text'
import { $setBlocksType } from '@lexical/selection'
import {
  INSERT_UNORDERED_LIST_COMMAND,
  INSERT_ORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from '@lexical/list'
import {
  $createParagraphNode,
  $getRoot,
  $getSelection,
  $isRangeSelection,
  FORMAT_TEXT_COMMAND,
  COMMAND_PRIORITY_LOW,
  type EditorState,
  type LexicalEditor as LexicalEditorType,
} from 'lexical'
import { HeadingNode, QuoteNode } from '@lexical/rich-text'
import { ListNode as ListNodeType, ListItemNode } from '@lexical/list'
import { LinkNode, AutoLinkNode } from '@lexical/link'
import { CodeNode } from '@lexical/code'
import { HorizontalRuleNode } from '@lexical/react/LexicalHorizontalRuleNode'

import { $convertFromMarkdownString, TRANSFORMERS } from '@lexical/markdown'
import { editorTheme } from './theme'
import { ImageNode } from './nodes/image-node'
import { MarkNode } from '@lexical/mark'
import { PageBreakNode } from './nodes/page-break-node'
import { LayoutContainerNode } from './nodes/layout-container-node'
import { LayoutItemNode } from './nodes/layout-item-node'
import { ImagePlugin, INSERT_IMAGE_COMMAND } from './plugins/image-plugin'
import { Button } from '@/components/ui/button'
import { Separator } from '@/components/ui/separator'
import { cn } from '@/lib/utils'
import {
  Bold,
  Italic,
  Underline,
  Strikethrough,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  ImageIcon,
} from 'lucide-react'
import '@/styles/lexical.css'

// ─── Props ──────────────────────────────────────────────────────

export interface MiniEditorProps {
  /** Initial Lexical editor state JSON */
  initialState?: unknown
  /** Plain text fallback if no JSON state exists */
  initialPlainText?: string
  /** Called on every change with Lexical JSON state */
  onChange?: (state: unknown) => void
  placeholder?: string
  /** Allow image paste/drop */
  images?: boolean
  projectId?: string
  /** Make editor read-only */
  readOnly?: boolean
  /** Min height CSS value */
  minHeight?: string
}

// ─── Mini Toolbar ───────────────────────────────────────────────

type BlockType = 'paragraph' | 'h2' | 'h3' | 'quote' | 'ul' | 'ol'

function MiniToolbar({ images }: { images?: boolean }) {
  const [editor] = useLexicalComposerContext()
  const { t } = useTranslation('editor')
  const [isBold, setIsBold] = useState(false)
  const [isItalic, setIsItalic] = useState(false)
  const [isUnderline, setIsUnderline] = useState(false)
  const [isStrikethrough, setIsStrikethrough] = useState(false)
  const [blockType, setBlockType] = useState<BlockType>('paragraph')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const updateToolbar = useCallback(() => {
    const selection = $getSelection()
    if (!$isRangeSelection(selection)) return

    setIsBold(selection.hasFormat('bold'))
    setIsItalic(selection.hasFormat('italic'))
    setIsUnderline(selection.hasFormat('underline'))
    setIsStrikethrough(selection.hasFormat('strikethrough'))

    const anchorNode = selection.anchor.getNode()
    const element =
      anchorNode.getKey() === 'root' ? anchorNode : anchorNode.getTopLevelElementOrThrow()

    if ($isHeadingNode(element)) {
      setBlockType(element.getTag() as BlockType)
    } else if ($isListNode(element)) {
      const listType = (element as ListNode).getListType()
      setBlockType(listType === 'bullet' ? 'ul' : 'ol')
    } else {
      const type = element.getType()
      setBlockType(type === 'quote' ? 'quote' : 'paragraph')
    }
  }, [])

  useEffect(() => {
    return editor.registerUpdateListener(({ editorState }) => {
      editorState.read(updateToolbar)
    })
  }, [editor, updateToolbar])

  const formatBlock = (type: BlockType) => {
    editor.update(() => {
      const selection = $getSelection()
      if (!$isRangeSelection(selection)) return

      if (type === 'paragraph') {
        $setBlocksType(selection, () => $createParagraphNode())
      } else if (type === 'h2') {
        if (blockType === 'h2') {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createHeadingNode('h2'))
        }
      } else if (type === 'h3') {
        if (blockType === 'h3') {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createHeadingNode('h3'))
        }
      } else if (type === 'quote') {
        if (blockType === 'quote') {
          $setBlocksType(selection, () => $createParagraphNode())
        } else {
          $setBlocksType(selection, () => $createQuoteNode())
        }
      } else if (type === 'ul') {
        if (blockType === 'ul') {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined)
        }
      } else if (type === 'ol') {
        if (blockType === 'ol') {
          editor.dispatchCommand(REMOVE_LIST_COMMAND, undefined)
        } else {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined)
        }
      }
    })
  }

  const handleImageUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    const src = URL.createObjectURL(file)
    editor.dispatchCommand(INSERT_IMAGE_COMMAND, { src, altText: file.name })
    e.target.value = ''
  }

  const ToolBtn = ({
    active,
    onClick,
    children,
    title,
  }: {
    active?: boolean
    onClick: () => void
    children: React.ReactNode
    title: string
  }) => (
    <Button
      variant="ghost"
      size="icon"
      className={cn('h-7 w-7', active && 'bg-accent text-accent-foreground')}
      onClick={onClick}
      type="button"
      title={title}
    >
      {children}
    </Button>
  )

  return (
    <div className="flex items-center gap-0.5 border-b px-2 py-1 flex-wrap">
      <ToolBtn
        active={isBold}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'bold')}
        title={t('toolbar.bold')}
      >
        <Bold className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={isItalic}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'italic')}
        title={t('toolbar.italic')}
      >
        <Italic className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={isUnderline}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'underline')}
        title={t('toolbar.underline')}
      >
        <Underline className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={isStrikethrough}
        onClick={() => editor.dispatchCommand(FORMAT_TEXT_COMMAND, 'strikethrough')}
        title={t('toolbar.strikethrough')}
      >
        <Strikethrough className="h-3.5 w-3.5" />
      </ToolBtn>

      <Separator orientation="vertical" className="mx-1 h-5" />

      <ToolBtn
        active={blockType === 'h2'}
        onClick={() => formatBlock('h2')}
        title={t('toolbar.heading2')}
      >
        <Heading2 className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={blockType === 'h3'}
        onClick={() => formatBlock('h3')}
        title={t('toolbar.heading3')}
      >
        <Heading3 className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={blockType === 'ul'}
        onClick={() => formatBlock('ul')}
        title={t('toolbar.bulletList')}
      >
        <List className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={blockType === 'ol'}
        onClick={() => formatBlock('ol')}
        title={t('toolbar.numberedList')}
      >
        <ListOrdered className="h-3.5 w-3.5" />
      </ToolBtn>
      <ToolBtn
        active={blockType === 'quote'}
        onClick={() => formatBlock('quote')}
        title={t('toolbar.quote')}
      >
        <Quote className="h-3.5 w-3.5" />
      </ToolBtn>

      {images && (
        <>
          <Separator orientation="vertical" className="mx-1 h-5" />
          <ToolBtn onClick={() => fileInputRef.current?.click()} title={t('toolbar.insertImage')}>
            <ImageIcon className="h-3.5 w-3.5" />
          </ToolBtn>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleImageUpload}
          />
        </>
      )}
    </div>
  )
}

// ─── Initial state plugin ───────────────────────────────────────

function MiniInitialStatePlugin({
  initialState,
  initialPlainText,
}: {
  initialState?: unknown
  initialPlainText?: string
}) {
  const [editor] = useLexicalComposerContext()
  const hasInitialized = useRef(false)

  useEffect(() => {
    if (hasInitialized.current) return
    hasInitialized.current = true

    if (initialState) {
      const editorState = editor.parseEditorState(JSON.stringify(initialState))
      editor.setEditorState(editorState)
    } else if (initialPlainText) {
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        $convertFromMarkdownString(initialPlainText, TRANSFORMERS, root)
      })
    }
  }, [editor, initialState, initialPlainText])

  return null
}

// ─── Main component ─────────────────────────────────────────────

function onError(error: Error) {
  console.error('MiniEditor error:', error)
}

export function MiniEditor({
  initialState,
  initialPlainText,
  onChange,
  placeholder,
  images = false,
  projectId,
  readOnly = false,
  minHeight = '120px',
}: MiniEditorProps) {
  const { t } = useTranslation('editor')
  const editorRef = useRef<LexicalEditorType | null>(null)

  const initialConfig = {
    namespace: 'MiniEditor',
    theme: editorTheme,
    nodes: [
      HeadingNode,
      QuoteNode,
      ListNodeType,
      ListItemNode,
      LinkNode,
      AutoLinkNode,
      CodeNode,
      HorizontalRuleNode,
      ImageNode,
      PageBreakNode,
      LayoutContainerNode,
      LayoutItemNode,
      MarkNode,
    ],
    onError,
    editable: !readOnly,
  }

  const handleChange = useCallback(
    (editorState: EditorState) => {
      if (onChange) {
        onChange(editorState.toJSON())
      }
    },
    [onChange]
  )

  return (
    <div className="mini-editor rounded-md border bg-background">
      <LexicalComposer initialConfig={initialConfig}>
        {!readOnly && <MiniToolbar images={images} />}
        <div className="relative" style={{ minHeight }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable className="mini-editor-content outline-none px-3 py-2 text-sm" />
            }
            placeholder={
              <div className="mini-editor-placeholder absolute top-2 left-3 text-sm text-muted-foreground pointer-events-none">
                {placeholder ?? t('placeholder.mini')}
              </div>
            }
            ErrorBoundary={LexicalErrorBoundary}
          />
        </div>
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin />
        <MarkdownShortcutPlugin transformers={TRANSFORMERS} />
        <EditorRefPlugin editorRef={editorRef} />
        <OnChangePlugin onChange={handleChange} ignoreSelectionChange />
        {images && <ImagePlugin projectId={projectId} />}
        <MiniInitialStatePlugin initialState={initialState} initialPlainText={initialPlainText} />
      </LexicalComposer>
    </div>
  )
}
